import { Plus } from "lucide-react";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";

/**
 * Fab — bouton d'action rapide flottant (« + »), primitive terrain transverse.
 *
 * Bouton 56×56 gold ancré en bas-droite (safe-area), atteignable au pouce d'une
 * main. Au tap, une feuille d'actions remonte au-dessus. PRÉSENTATION PURE : la
 * liste d'`actions` est déjà filtrée par permission + contexte PAR LE CALLER
 * (shell/page) — le Fab ne connaît ni le routeur ni les droits. Sans action
 * disponible, il ne s'affiche pas (pas de bouton fantôme).
 *
 * Accessibilité / terrain : cibles ≥ 56px, `aria-expanded`/`aria-haspopup`,
 * fermeture au clic extérieur + Échap, animations respectant
 * `prefers-reduced-motion` (variantes `motion-reduce:`).
 */
export interface FabAction {
  /** Libellé de l'action (ex. « Pesée »). */
  label: string;
  /** Icône optionnelle (lucide, 18px conseillé). */
  icon?: ReactNode;
  /** Ligne de description optionnelle sous le libellé. */
  description?: string;
  /** Déclenché au tap. La navigation éventuelle est gérée par le caller. */
  onSelect: () => void;
}

export interface FabProps {
  /** Actions déjà filtrées (permission + contexte) fournies par le caller. */
  actions: FabAction[];
  /** Libellé accessible du bouton fermé (défaut « Action rapide »). */
  label?: string;
}

export function Fab({ actions, label = "Action rapide" }: FabProps) {
  const [ouvert, setOuvert] = useState(false);
  const conteneurRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  // Fermeture au clic extérieur + Échap. Le Fab est une aide, pas une modale
  // bloquante : on ne piège pas le focus (le vigneron garde la main sur l'écran).
  useEffect(() => {
    if (!ouvert) return;
    function onPointerDown(event: PointerEvent) {
      if (conteneurRef.current && !conteneurRef.current.contains(event.target as Node)) {
        setOuvert(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOuvert(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [ouvert]);

  // Aucune action possible (droits/contexte) → aucun bouton (règle honnêteté).
  if (actions.length === 0) return null;

  return (
    <div
      ref={conteneurRef}
      className="fixed bottom-0 right-0 z-40 flex flex-col items-end gap-3"
      // Marge + encoche/safe-area (mobiles à bords arrondis) : le bouton reste
      // atteignable sans être mangé par la barre système.
      style={{
        paddingBottom: "calc(1rem + env(safe-area-inset-bottom))",
        paddingRight: "calc(1rem + env(safe-area-inset-right))",
        paddingLeft: "1rem",
        paddingTop: "1rem",
      }}
    >
      {ouvert && (
        <ul id={menuId} role="menu" className="flex w-60 flex-col gap-2">
          {actions.map((action) => (
            <li key={action.label} role="none">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOuvert(false);
                  action.onSelect();
                }}
                className="flex min-h-[56px] w-full items-center gap-3 rounded-xl border border-champagne-200 bg-white px-4 py-2 text-left shadow-lg transition hover:border-champagne-300 hover:bg-champagne-50/60 motion-reduce:transition-none"
              >
                {action.icon && (
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-champagne-50 text-champagne-700 ring-1 ring-champagne-200">
                    {action.icon}
                  </span>
                )}
                <span className="min-w-0">
                  <span className="block text-[14px] font-semibold text-aubergine-900">
                    {action.label}
                  </span>
                  {action.description && (
                    <span className="block truncate text-[12px] text-champagne-700">
                      {action.description}
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={ouvert}
        aria-controls={ouvert ? menuId : undefined}
        aria-label={ouvert ? "Fermer les actions rapides" : label}
        onClick={() => setOuvert((valeur) => !valeur)}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-champagne-600 text-white shadow-xl ring-1 ring-champagne-700/20 transition hover:bg-champagne-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne-400 motion-reduce:transition-none"
      >
        {/* « + » qui pivote en « × » à l'ouverture (transition désactivée si
            l'utilisateur a demandé moins de mouvement). */}
        <span
          className="transition-transform motion-reduce:transition-none"
          style={{ transform: ouvert ? "rotate(45deg)" : "none" }}
        >
          <Plus size={26} aria-hidden="true" />
        </span>
      </button>
    </div>
  );
}
